/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Grid } from '@mui/material';
import { Info, ReportAdd } from '@jda/lui-common-icon-library-mui5';
import CustomSearch from '../CustomSearch';
import PasswordPolicyTable from './PasswordPolicyTable/PasswordPolicyTable';
import { useStyles } from './styles';
import { fetchPasswordPolicies, fetchPasswordPoliciesRule } from '../../store/passwordPolicies/passwordPolicies';
import { fetchUpdateFormValue } from '../../store/passwordPolicies/passwordPoliciesActionCreator';
import { selectFormValues, selectPasswordPolicies } from '../../store/passwordPolicies/passwordPoliciesSelector';
import { selectResourcePermissions } from '../../store/auth/authSelector';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { actions, resources } from '../../shared/CheckPermissions/constants';
import CustomizedDialogs from './Dialog';
import PasswordPolicyForm from './PasswordPolicyForm';
import { PasswordPolicyModel } from '../../models/PasswordPolicy/PasswordPolicy';
import { createPasswordPolicyRule, updatePasswordPolicyRule } from '../../services/setting';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { NATIVE_CAMEL_CASE, NATIVE_UPPER_CASE } from '../../container/Settings/constants';
import {
  ADD_POLICY,
  SEARCH_PASSWORD_POLICIES,
  formValuesPasswordPolicyDefault,
  ADD,
  SAVE,
  CANCEL,
  SAVED_MESSAGE_SUCCESS,
  SAVED_MESSAGE_ERROR,
  ERROR_IN_FETCHING_IDENTITY_PROVIDERS,
  ENABLE_BY_USER_STORE_MESSAGE,
  EDIT
} from './constants';
import { NO_LIMIT } from './PasswordPolicyTable/PasswordPolicyDetails/constants';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';
import { LuiToolbar } from '@jda/lui-common-component-library-mui5';
import { fetchIdps } from '../../store/user/users';

const PasswordPolicies = () => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [dialogAction, setDialogAction] = useState({ id: '', action: '' });
  const [nativeIDP, setNativeIDP] = useState();
  const [showLoader, setShowLoader] = useState(true);
  const selectedFormValues = useSelector(selectFormValues);
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const defaultPolicy = useMemo(() => {
    const idpPolicy = passwordPolicies.find((policy: any) => policy.idpsNames.length > 0);
    const defaultPolicy = passwordPolicies.find((policy: any) => policy.isDefault);
    return idpPolicy || defaultPolicy;
  }, [passwordPolicies])

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const passwordPolicyCreatePermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.CREATE, resourcePermissions.resources[resources.PASSWORD_POLICY]);

  useEffect(() => {
    fetchIdps(dispatch)
      .then((idps: any) => {
        if (idps && idps.length > 0) {
          const nativeIDP = idps.find((idp: any) => idp.idpType === NATIVE_CAMEL_CASE || idp.type === NATIVE_UPPER_CASE);
          nativeIDP && setNativeIDP(nativeIDP);
          setShowLoader(false);
        }
      })
      .catch((_error: any) => {
        displaySnackbar(ERROR_IN_FETCHING_IDENTITY_PROVIDERS, 'error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    nativeIDP && fetchPasswordPolicies(dispatch);
  }, [dispatch, nativeIDP]);

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };

  const getPasswordPolicyRules = (data: PasswordPolicyModel) => {
    if (data) {
      fetchPasswordPoliciesRule(dispatch, data.id);
    }
  };

  const handleClickOpenDialog = async (action: string, data: any) => {
    if (action === ADD) {
      const defaultPolicy = passwordPolicies.find((entity: any) => entity.isDefault === true);
      await getPasswordPolicyRules(defaultPolicy);
      setDialogAction({ id: defaultPolicy.id, action: ADD });
    } else {
      await getPasswordPolicyRules(data);
      setDialogAction({ id: data.id, action: EDIT });
    }
    setIsOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    fetchPasswordPolicies(dispatch);
    dispatch(fetchUpdateFormValue(formValuesPasswordPolicyDefault));
  };

  const validateForm = () => {
    const fields = [
      { field: selectedFormValues.policyName, name: 'policyName' },
      { field: selectedFormValues.minLength, name: 'minLength' },
      { field: selectedFormValues.maxLength, name: 'maxLength' },
      { field: selectedFormValues.expiryDuration, name: 'expiryDuration' },
      { field: selectedFormValues.maxConsecutiveLetters, name: 'maxConsecutiveLetters' }
    ];

    let valid = true;
    fields.forEach(({ field, name }) => {
      if (!field.value) {
        dispatch(fetchUpdateFormValue({ [name]: { value: '', error: true, errorHelperText: 'Required' } }));
        valid = false;
      }
    });
    if (selectedFormValues.passwordHistoryEnabled) {
      if (!selectedFormValues.oldPasswords?.value) {
        dispatch(fetchUpdateFormValue({ oldPasswords: { value: '', error: true, errorHelperText: 'Required' } }));
        valid = false;
      } else if (selectedFormValues.oldPasswords?.value < 1) {
        dispatch(
          fetchUpdateFormValue({
            oldPasswords: { value: '', error: true, errorHelperText: 'Value must be greater than 0' }
          })
        );
        valid = false;
      }
    }
    return valid;
  };

  const handleSaveDialog = async () => {
    if (validateForm()) {
      const data: any = {
        name: selectedFormValues.policyName.value,
        minLength: selectedFormValues.minLength.value,
        maxLength: selectedFormValues.maxLength.value,
        expiryDuration: selectedFormValues.expiryDuration.value,
        includeNumbers: selectedFormValues.includeNumbers,
        includeSpecialCharacters: selectedFormValues.includeSpecialCharacters,
        lettersInMixedCase: selectedFormValues.lettersInMixedCase,
        excludePasswordsInDictionary: selectedFormValues.excludeCommonUnsafePassword,
        excludeUserProfileInfoInPassword: selectedFormValues.excludeUserProfileInformation,
        passwordHistoryEnabled: selectedFormValues.passwordHistoryEnabled,
        oldPasswords: selectedFormValues.oldPasswords?.value || '0'
      };
      if (selectedFormValues.maxConsecutiveLetters.value !== NO_LIMIT) {
        data.maxConsecutiveLetters = selectedFormValues.maxConsecutiveLetters.value;
      }
      try {
        if (dialogAction.action === ADD) {
          await createPasswordPolicyRule(data);
          handleCloseDialog();
        } else {
          await updatePasswordPolicyRule(dialogAction.id, data);
          handleCloseDialog();
        }
        displaySnackbar(SAVED_MESSAGE_SUCCESS, 'success');
      } catch (error) {
        displaySnackbar(SAVED_MESSAGE_ERROR, 'error');
      }
      fetchPasswordPolicies(dispatch);
    }
  };

  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.searchBarWrap}>
          <CustomSearch placeHolder={SEARCH_PASSWORD_POLICIES} value={searchValue} onSearchChange={setSearchValue} />
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box>
          {passwordPolicyCreatePermission && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<ReportAdd />}
              data-testid={'add-policy-button'}
              onClick={() => handleClickOpenDialog(ADD, null)}
            >
              {ADD_POLICY}
            </Button>
          )}
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12} className={classes.passwordPolicyWrapper}>
        {!showLoader && !nativeIDP && (
          <Grid container className={classes.loader}>
            <Alert icon={<Info />} elevation={0} severity={'info'} variant={'outlined'}>
              {ENABLE_BY_USER_STORE_MESSAGE}
            </Alert>
          </Grid>
        )}
        <Box className={classes.passwordPolicies}>
          <PasswordPolicyTable
            nativeIDP={nativeIDP}
            searchValue={searchValue}
            extendedToolbar={extendedToolbar}
            handleClickOpenDialog={handleClickOpenDialog}
          />
          <CustomizedDialogs
            isOpenDialog={isOpenDialog}
            handleCloseDialog={handleCloseDialog}
            dialogTitle={'Password Policy'}
            dialogActionButton1={
              <Button variant="outlined" color="primary" size="small" onClick={handleCloseDialog} data-testid={'cancel-button'}>
                {CANCEL}
              </Button>
            }
            dialogActionButton2={
              <Button variant="contained" color="primary" size="small" onClick={handleSaveDialog} data-testid={'save-button'}>
                {SAVE}
              </Button>
            }
          >
            <PasswordPolicyForm defaultPolicy={defaultPolicy} />
          </CustomizedDialogs>
          <SnackbarAlert />
        </Box>
      </Grid>
    </Grid>
  );
};
const PasswordPolicy = {
  component: PasswordPolicies,
  name: 'Password Policies'
};

export default PasswordPolicy;
